/**
 * Pre-styled layout Box components for common patterns.
 *
 * These components eliminate repetitive sx props and provide consistent layouts.
 * They use MUI's styled() API and automatically pick up theme values.
 *
 * Usage:
 * ```tsx
 * import { FlexColumn, FlexRow, PanelContainer } from '../components/LayoutBoxes';
 *
 * // Before:
 * <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
 *
 * // After:
 * <FlexColumn gap={2}>
 * ```
 */

import { Box, BoxProps, styled } from '@mui/material';

/**
 * Basic flex column container.
 * @example <FlexColumn gap={2}>content</FlexColumn>
 */
export const FlexColumn = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
});

/**
 * Flex column with centered content (both axes).
 * Perfect for empty states, loading states, centered content.
 * @example <FlexColumnCenter>Loading...</FlexColumnCenter>
 */
export const FlexColumnCenter = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
});

/**
 * Flex column that stretches to fill parent height.
 * Common for panels that need to fill available space.
 * @example <FlexColumnStretch>Panel content</FlexColumnStretch>
 */
export const FlexColumnStretch = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
});

/**
 * Basic flex row with vertically centered items.
 * @example <FlexRow gap={1}>items</FlexRow>
 */
export const FlexRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
});

/**
 * Flex row with centered content (both axes).
 * @example <FlexRowCenter>centered content</FlexRowCenter>
 */
export const FlexRowCenter = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

/**
 * Flex row with space-between, full width.
 * Perfect for headers with title + actions.
 * @example <FlexRowBetween><Title /><Actions /></FlexRowBetween>
 */
export const FlexRowBetween = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
});

/**
 * Flex row with content aligned to the end.
 * Perfect for button containers.
 * @example <FlexRowEnd><Button>Save</Button></FlexRowEnd>
 */
export const FlexRowEnd = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
});

/**
 * Panel container with alt background color.
 * Use for content panels (transcript, document, context).
 * @example <PanelContainer>panel content</PanelContainer>
 */
export const PanelContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  backgroundColor: theme.palette.custom.backgroundAlt,
  padding: theme.spacing(0.5),
  gap: theme.spacing(1),
}));

/**
 * Card container with border and subtle shadow.
 * Use for elevated content cards.
 * @example <CardContainer>card content</CardContainer>
 */
export const CardContainer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: theme.palette.custom.subtleShadow,
}));

/**
 * Main app container that fills viewport.
 * Use at root level of widget.
 */
export const AppContainer = styled(Box)(({ theme }) => ({
  height: '100%',
  minWidth: '350px',
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  position: 'relative',
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.primary,
}));

/**
 * Full-screen dialog overlay.
 * Positions absolutely within parent with high z-index.
 * @example <DialogOverlay><DialogContent>...</DialogContent></DialogOverlay>
 */
export const DialogOverlay = styled(Box)({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 999999,
  pointerEvents: 'auto',
});

/**
 * Dialog content container with rounded corners.
 * Use inside DialogOverlay.
 */
export const DialogContentBox = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: '8px',
  width: '100%',
  height: '100%',
  overflow: 'auto',
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
}));

/**
 * Empty state container - centered with padding.
 * Use for "no data" or "no results" states.
 * @example <EmptyState><EmptyIcon /><EmptyText /></EmptyState>
 */
export const EmptyState = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  flex: 1,
  padding: theme.spacing(3),
  gap: theme.spacing(1.5),
  color: theme.palette.text.secondary,
}));

/**
 * Sticky footer for action buttons.
 * Automatically positions at bottom with border-top.
 * @example <StickyFooter><Button>Action</Button></StickyFooter>
 */
export const StickyFooter = styled(Box)(({ theme }) => ({
  padding: '12px 16px',
  borderTop: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  boxSizing: 'border-box',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(0.5),
  flexShrink: 0,
}));

/**
 * Scrollable container with custom scrollbar styling.
 * Use for lists or content that may overflow.
 * @example <ScrollableContainer>long content</ScrollableContainer>
 */
export const ScrollableContainer = styled(Box)(({ theme }) => ({
  overflowY: 'auto',
  overflowX: 'hidden',
  flex: 1,
  minHeight: 0,
  '&::-webkit-scrollbar': {
    width: '4px',
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: theme.palette.custom.scrollbarTrack,
    borderRadius: '2px',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: theme.palette.custom.scrollbarThumb,
    borderRadius: '2px',
    '&:hover': {
      backgroundColor: theme.palette.custom.scrollbarThumbHover,
    },
  },
}));

/**
 * Tab container with bottom border.
 * Use for tab navigation areas.
 */
export const TabContainer = styled(Box)(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.divider}`,
  position: 'relative',
  zIndex: 999,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  backgroundColor: theme.palette.background.paper,
}));

/**
 * Circular icon container.
 * Use for avatar-style icon displays.
 * @param size - Override default 48px size
 * @example <IconCircle><PersonIcon /></IconCircle>
 */
export const IconCircle = styled(Box)<BoxProps & { size?: number }>(
  ({ theme, size = 48 }) => ({
    width: size,
    height: size,
    borderRadius: '50%',
    backgroundColor: theme.palette.primary.main,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: theme.palette.custom.cardShadow,
    color: theme.palette.custom.contrastText,
  })
);

/**
 * Secondary icon circle with hover background.
 * Use for patient avatars and similar.
 */
export const IconCircleSecondary = styled(Box)<BoxProps & { size?: number }>(
  ({ theme, size = 55 }) => ({
    width: size,
    height: size,
    borderRadius: '50%',
    backgroundColor: theme.palette.custom.neutralBg,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  })
);

/**
 * Pulsing dot indicator.
 * Use for recording/active states.
 * @param color - Override default error (red) color
 * @example <PulseDot /> // Red pulsing dot
 * @example <PulseDot color="primary" /> // Primary color dot
 */
export const PulseDot = styled(Box)<BoxProps & { color?: 'primary' | 'error' }>(
  ({ theme, color = 'error' }) => ({
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor:
      color === 'primary'
        ? theme.palette.primary.main
        : theme.palette.error.main,
    animation: 'pulse 1.5s infinite',
    '@keyframes pulse': {
      '0%': { opacity: 1 },
      '50%': { opacity: 0.5 },
      '100%': { opacity: 1 },
    },
  })
);

/**
 * Small pulsing dot (6px) with scale animation.
 * Use for subtle active indicators.
 */
export const PulseDotSmall = styled(Box)(({ theme }) => ({
  width: '6px',
  height: '6px',
  borderRadius: '50%',
  backgroundColor: theme.palette.primary.main,
  animation: 'pulseScale 1.2s infinite ease-in-out',
  '@keyframes pulseScale': {
    '0%, 100%': { opacity: 1, transform: 'scale(1)' },
    '50%': { opacity: 0.6, transform: 'scale(0.8)' },
  },
}));

/**
 * Vertical divider for separating inline elements.
 */
export const VerticalDivider = styled(Box)(({ theme }) => ({
  width: '1px',
  height: '16px',
  backgroundColor: theme.palette.divider,
  flexShrink: 0,
}));

export type LayoutBoxComponents = {
  FlexColumn: typeof FlexColumn;
  FlexColumnCenter: typeof FlexColumnCenter;
  FlexColumnStretch: typeof FlexColumnStretch;
  FlexRow: typeof FlexRow;
  FlexRowCenter: typeof FlexRowCenter;
  FlexRowBetween: typeof FlexRowBetween;
  FlexRowEnd: typeof FlexRowEnd;
  PanelContainer: typeof PanelContainer;
  CardContainer: typeof CardContainer;
  AppContainer: typeof AppContainer;
  DialogOverlay: typeof DialogOverlay;
  DialogContentBox: typeof DialogContentBox;
  EmptyState: typeof EmptyState;
  StickyFooter: typeof StickyFooter;
  ScrollableContainer: typeof ScrollableContainer;
  TabContainer: typeof TabContainer;
  IconCircle: typeof IconCircle;
  IconCircleSecondary: typeof IconCircleSecondary;
  PulseDot: typeof PulseDot;
  PulseDotSmall: typeof PulseDotSmall;
  VerticalDivider: typeof VerticalDivider;
};
